import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from './images/logo.png';

import {
  HomeIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  UsersIcon,
  TruckIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { GlobalContext } from './context/GlobalContext';
import { GiPalmTree } from 'react-icons/gi';

const menuItems = [
  { name: 'Dashboard', icon: HomeIcon, path: '/' },
  { name: 'Messenger', icon: ChatBubbleLeftRightIcon, path: '/messenger' },
  { name: 'My VCP', icon: UsersIcon, path: '/my-vcp' },
  { name: 'My VCP Management', icon: ChartBarIcon, path: '/my-vcp/management' },
  // { name: 'Sales', icon: ShoppingCartIcon, path: '/sales' }, // Commented out - now in Reports section
  { name: 'Customer Manager', icon: UserGroupIcon, path: '/customers' },
  // { name: 'Workshop Essentials', icon: TruckIcon, path: '/workshopessen' }, // Moved below to appear before User Management
  { name: 'My Holidays', icon: GiPalmTree, path: '/myholidays' }
];

const settingsSubItems = [
  { name: 'Staff', path: '/settings/staff' },
  { name: 'Branch', path: '/settings/branch' },
  { name: 'Assign Cust', path: '/settings/assigncust' },
  { name: 'Messages', path: '/settings/messages' },
  { name: 'Shifts', path: '/settings/shifts' },
  { name: 'Holidays', path: '/settings/holidays' },
  { name: 'About', path: '/settings/about' }
];

const tmaSubItems = [
  { name: 'Calendar', path: '/tma/calendar' },
  { name: 'Holidays', path: '/tma/holidays' },
  { name: 'Shifts', path: '/tma/shifts' }
];

const bdmSubItems = [
  { name: 'Visits', path: '/repvisits' },
  { name: 'Rep Visit Log', path: '/repvisits/logs' }
];

const fleetSubItems = [
  { name: 'Vehicles', path: '/fleet/vehicles' },
  { name: 'Drivers', path: '/fleet/drivers' },
  { name: 'Incidents', path: '/fleet/incidents' },
  { name: 'Fuel Logs', path: '/fleet/fuel' }
];

const salesAnalysisSubItems = [
  { name: 'Daily Sales', path: '/reports/sales-analysis?type=daily-sales' },
  { name: 'Weekly Sales', path: '/reports/sales-analysis?type=weekly-sales' },
  { name: 'Invoice Summary', path: '/reports/sales-analysis?type=invoice-summary' },
  { name: '6M Sales (Area)', path: '/reports/sales-analysis?type=six-month-sales' },
  { name: 'Area Analysis', path: '/reports/sales-analysis?type=area-analysis' },
  { name: 'Quotes Report', path: '/reports/sales-analysis?type=quotes-report' }
];

const advancedReportsSubItems = [
  { name: 'Reports Overview', path: '/reports' },
  { name: 'Branch Performance', path: '/reports/branch' },
  { name: 'Area Comparison', path: '/reports/area-comparison' },
  { name: 'Customer Analysis', path: '/reports/customer-analysis' },
  { name: 'Advisor Performance', path: '/reports/advisor' },
  { name: 'BDM Activity', path: '/reports/bdm' },
  { name: 'Customer Contacts', path: '/reports/customer-contacts' },
  { 
    name: 'Sales Analysis', 
    path: '/reports/sales-analysis',
    subItems: salesAnalysisSubItems
  }
];

// Centralized permission management
const menuPermissions = {
  '/': ['admin', 'bm', 'sales', 'rep'],
  '/messenger': ['admin', 'bm', 'sales', 'rep'],
  '/my-vcp': ['admin', 'bm', 'sales', 'rep'],
  '/my-vcp/management': ['admin', 'bm'],
  '/sales': ['admin', 'bm', 'sales', 'rep'],
  '/customers': ['admin', 'bm', 'rep', 'sales'],
  '/repvisits': ['admin', 'bm', 'rep'],
  '/repvisits/logs': ['admin', 'bm', 'rep'],
  '/workshopessen': ['admin', 'bm', 'rep'],
  '/myholidays': [],

  // TMA Section
  '/tma/calendar': [],
  '/tma/holidays': [],
  '/tma/shifts': [],

  // Settings Section
  '/settings': ['admin'], // Main settings page - admin only
  '/settings/staff': ['admin'],
  '/settings/branch': ['admin'],
  '/settings/assigncust': ['admin'],
  '/settings/messages': ['admin'],
  '/settings/shifts': ['admin'],
  '/settings/holidays': ['admin'],
  '/settings/about': ['admin', 'bm'],
  '/settings/users': ['admin'],

  // Fleet Management (restricted to Admin, Branch Manager, and Fleet users)
  '/fleet/vehicles': ['admin', 'bm', 'fleet'],
  '/fleet/drivers': ['admin', 'bm', 'fleet'],
  '/fleet/incidents': ['admin', 'bm', 'fleet'],
  '/fleet/fuel': ['admin', 'bm', 'fleet'],

  // Advanced Reports (restricted to Admin, Branch Manager, and Sales users)
  '/reports': ['admin', 'bm', 'sales'],
  '/reports/branch': ['admin', 'bm', 'sales'],
  '/reports/customer-analysis': ['admin', 'bm', 'sales'],
  '/reports/advisor': ['admin', 'bm', 'sales'],
  '/reports/bdm': ['admin', 'bm', 'sales'],
  '/reports/customer-contacts': ['admin', 'bm', 'sales'],
  '/reports/area-comparison': ['admin', 'bm', 'sales'],
  '/reports/sales-analysis': ['admin', 'bm', 'sales']
};

export default function Sidebar({ user, onCollapseChange }) {
  const navigate = useNavigate();
  const location = window.location.pathname;
  const firstSeg = location.split('/')[1];
  const submenuDefault = ['settings', 'tma', 'fleet', 'reports'].includes(firstSeg)
    ? firstSeg
    : firstSeg === 'repvisits'
    ? 'bdm'
    : null;
  const [openSubmenu, setOpenSubmenu] = useState(submenuDefault);
  const [openNestedSubmenu, setOpenNestedSubmenu] = useState(null);
  const { cinfo, apir, uinfo } = useContext(GlobalContext);
  
  // Check if screen is mobile on mount and set collapsed state accordingly
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768; // md breakpoint
    }
    return false;
  });

  // Update collapsed state when window is resized
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Notify parent component when collapse state changes
  React.useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
  }, [collapsed, onCollapseChange]);

  // Close submenus when navigating away from their items (only react to location changes)
  React.useEffect(() => {
    const currentPath = location.split('?')[0];
    
    // If navigating to Dashboard, close all submenus
    if (currentPath === '/') {
      setOpenSubmenu(null);
      setOpenNestedSubmenu(null);
      return;
    }
    
    // Check if current path matches any Reports submenu items (including nested)
    const reportsItems = advancedReportsSubItems || [];
    const isInReports = reportsItems.some(item => {
      // Check if current path matches the item itself
      const itemPath = item.path.split('?')[0];
      if (currentPath === itemPath) return true;
      
      // Check nested subItems (e.g., Sales Analysis submenu)
      if (item.subItems) {
        return item.subItems.some(sub => {
          const subPath = sub.path.split('?')[0];
          return currentPath === subPath;
        });
      }
      return false;
    });
    
    // If not in Reports and Reports is open, close it
    if (!isInReports) {
      setOpenSubmenu(prev => prev === 'reports' ? null : prev);
      setOpenNestedSubmenu(prev => prev ? null : prev);
    }
    
    // Check other submenus similarly
    const settingsItems = settingsSubItems || [];
    const isInSettings = settingsItems.some(item => {
      const itemPath = item.path.split('?')[0];
      return currentPath === itemPath;
    });
    if (!isInSettings) {
      setOpenSubmenu(prev => prev === 'settings' ? null : prev);
    }

    const bdmItems = bdmSubItems || [];
    const isInBdm = bdmItems.some(item => {
      const itemPath = item.path.split('?')[0];
      return currentPath === itemPath;
    });
    if (!isInBdm) {
      setOpenSubmenu(prev => prev === 'bdm' ? null : prev);
    }
    
    const tmaItems = tmaSubItems || [];
    const isInTma = tmaItems.some(item => {
      const itemPath = item.path.split('?')[0];
      return currentPath === itemPath;
    });
    if (!isInTma) {
      setOpenSubmenu(prev => prev === 'tma' ? null : prev);
    }
    
    const fleetItems = fleetSubItems || [];
    const isInFleet = fleetItems.some(item => {
      const itemPath = item.path.split('?')[0];
      return currentPath === itemPath;
    });
    if (!isInFleet) {
      setOpenSubmenu(prev => prev === 'fleet' ? null : prev);
    }
  }, [location]);

  const handleSetSubmenu = (label) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenSubmenu(label);
    } else {
      setOpenSubmenu(openSubmenu === label ? null : label);
    }
  };

  // Permission check function
  const canAccess = (status, path) => {
    return menuPermissions[path]?.includes(status) || false;
  };

  return (
    <div
      className={`flex flex-col ${collapsed ? 'w-[70px]' : 'w-[260px] md:w-[260px]'} h-screen bg-gray-800 transition-all duration-300 z-20 border-r border-gray-700`}
    >
      {/* Fixed Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-4 border-b border-gray-700 flex-shrink-0">
        {!collapsed && <img src={`${apir}/public/images/Logo.png`} style={{ width: '45%' }} alt="Value Car Parts Logo" />}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-gray-500 hover:text-gray-300 focus:outline-none p-1.5 rounded-md hover:bg-gray-700/50 transition-colors"
        >
          {collapsed ?
            <ChevronRightIcon className="w-5 h-5" /> :
            <ChevronLeftIcon className="w-5 h-5" />
          }
        </button>
      </div>

      {/* Scrollable Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden mt-4 px-2">
        <ul className="space-y-1">
          {/* Show Dashboard first for non-fleet users */}
          {uinfo?.Status !== 'fleet' && canAccess(uinfo?.Status, '/') && (
            <MenuItem
              item={menuItems[0]}
              collapsed={collapsed}
            />
          )}

          {/* Messenger - Second item */}
          {uinfo?.Status !== 'fleet' && canAccess(uinfo?.Status, '/messenger') && (
            <MenuItem
              item={menuItems[1]}
              collapsed={collapsed}
            />
          )}

          {/* Reports Submenu - Third item */}
          {uinfo?.Status !== 'fleet' && advancedReportsSubItems.some(item => canAccess(uinfo?.Status, item.path)) && (
            <SubMenu
              label="Reports"
              value="reports"
              items={advancedReportsSubItems.filter((item) => canAccess(uinfo?.Status, item.path))}
              Icon={ChartBarIcon}
              openSubmenu={openSubmenu}
              handleOpenSubmenu={() => handleSetSubmenu('reports')}
              collapsed={collapsed}
              openNestedSubmenu={openNestedSubmenu}
              setOpenNestedSubmenu={setOpenNestedSubmenu}
            />
          )}

          {/* Show remaining menu items for non-fleet users */}
          {uinfo?.Status !== 'fleet' && menuItems.slice(2).map((item, index) =>
            canAccess(uinfo?.Status, item.path) ? (
              <React.Fragment key={index + 2}>
                <MenuItem
                  item={item}
                  collapsed={collapsed}
                />
                {item.path === '/customers' && bdmSubItems.some(subItem => canAccess(uinfo?.Status, subItem.path)) && (
                  <SubMenu
                    label="BDM Manager"
                    value="bdm"
                    items={bdmSubItems.filter((subItem) => canAccess(uinfo?.Status, subItem.path))}
                    Icon={UserGroupIcon}
                    openSubmenu={openSubmenu}
                    handleOpenSubmenu={() => handleSetSubmenu('bdm')}
                    collapsed={collapsed}
                  />
                )}
              </React.Fragment>
            ) : null
          )}

          {/* Show only fleet menu for fleet users */}
          {uinfo?.Status === 'fleet' && (
            <MenuItem
              item={{ name: 'Fleet Dashboard', icon: HomeIcon, path: '/fleet/vehicles' }}
              collapsed={collapsed}
            />
          )}

          {uinfo?.Status !== 'fleet' && tmaSubItems.some(item => canAccess(uinfo?.Status, item.path)) && (
            <SubMenu
              label="TMA"
              value="tma"
              items={tmaSubItems.filter((item) => canAccess(uinfo?.Status, item.path))}
              Icon={CalendarIcon}
              openSubmenu={openSubmenu}
              handleOpenSubmenu={() => handleSetSubmenu('tma')}
              collapsed={collapsed}
            />
          )}

          {/* Show fleet submenu for non-fleet users who have access */}
          {uinfo?.Status !== 'fleet' && canAccess(uinfo?.Status, '/fleet/vehicles') && (
            <SubMenu
              label="Fleet"
              value="fleet"
              items={fleetSubItems.filter((item) => canAccess(uinfo?.Status, item.path))}
              Icon={TruckIcon}
              openSubmenu={openSubmenu}
              handleOpenSubmenu={() => handleSetSubmenu('fleet')}
              collapsed={collapsed}
            />
          )}

          {/* Show fleet submenu for fleet users */}
          {uinfo?.Status === 'fleet' && (
            <SubMenu
              label="Fleet"
              value="fleet"
              items={fleetSubItems.filter((item) => canAccess(uinfo?.Status, item.path))}
              Icon={TruckIcon}
              openSubmenu={openSubmenu}
              handleOpenSubmenu={() => handleSetSubmenu('fleet')}
              collapsed={collapsed}
            />
          )}

          {/* Workshop Essentials - moved here to appear before User Management */}
          {uinfo?.Status !== 'fleet' && canAccess(uinfo?.Status, '/workshopessen') && (
            <MenuItem
              item={{ name: 'Workshop Essentials', icon: TruckIcon, path: '/workshopessen' }}
              collapsed={collapsed}
            />
          )}

          {/* Settings SubMenu - with dropdown */}
          {uinfo?.Status !== 'fleet' && canAccess(uinfo?.Status, '/settings') && (
            <SubMenu
              label="Settings"
              value="settings"
              items={settingsSubItems.filter((item) => canAccess(uinfo?.Status, item.path))}
              Icon={CogIcon}
              openSubmenu={openSubmenu}
              handleOpenSubmenu={() => handleSetSubmenu('settings')}
              collapsed={collapsed}
            />
          )}

          {/* User Management - Admin only */}
          {canAccess(uinfo?.Status, '/settings/users') && (
            <MenuItem
              item={{ name: 'User Management', icon: UsersIcon, path: '/settings/users' }}
              collapsed={collapsed}
            />
          )}
        </ul>
      </nav>

      {/* Fixed Footer - AA logo at bottom */}
      <div className="p-6 flex justify-center flex-shrink-0 border-t border-gray-700">
        {!collapsed && <img className="object-cover w-40" src={logo} alt="AA Logo" />}
      </div>

    </div>
  );
}

const MenuItem = ({ item, collapsed }) => {
  const location = window.location.pathname;
  const isActive = location === item.path;
  
  return (
    <li className={`relative ${collapsed ? 'px-2' : 'px-4'} py-0.5`}>
      <Link
        to={item.path}
        className={`inline-flex items-center ${collapsed ? 'justify-center' : ''} w-full text-sm font-medium transition-all duration-200 rounded-md px-3 py-2.5 ${
          isActive 
            ? 'bg-gray-700 text-gray-100 border-l-4 border-l-blue-500 shadow-sm' 
            : 'text-gray-300 hover:bg-gray-700/50 hover:text-gray-200'
        }`}
        title={collapsed ? item.name : ''}
      >
        <item.icon className={`w-6 h-6 ${isActive ? 'text-gray-100' : 'text-gray-200'}`} />
        {!collapsed && <span className={`ml-3 ${isActive ? 'text-gray-100' : 'text-gray-300'}`}>{item.name}</span>}
      </Link>
    </li>
  );
};

const SubMenu = ({ label, value, items, Icon, openSubmenu, handleOpenSubmenu, collapsed, openNestedSubmenu, setOpenNestedSubmenu }) => {
  const location = window.location.pathname;
  const isActive = items.some(item => {
    if (item.subItems) {
      return item.subItems.some(sub => {
        // Handle query params in path
        const subPath = sub.path.split('?')[0];
        const currentPath = location.split('?')[0];
        return currentPath === subPath;
      });
    }
    // Handle query params in path
    const itemPath = item.path.split('?')[0];
    const currentPath = location.split('?')[0];
    return currentPath === itemPath;
  });
  const isOpen = openSubmenu === value;
  
  return (
    <li className={`relative ${collapsed ? 'px-2' : 'px-4'} py-0.5`}>
      <button
        onClick={handleOpenSubmenu}
        className={`inline-flex items-center ${collapsed ? 'justify-center' : ''} w-full text-sm font-medium transition-all duration-200 rounded-md px-3 py-2.5 focus:outline-none ${
          isActive
            ? 'bg-gray-700 text-gray-100 border-l-4 border-l-purple-500 shadow-sm' 
            : isOpen
            ? 'bg-gray-700/40 text-gray-300'
            : 'text-gray-300 hover:bg-gray-700/50 hover:text-gray-200'
        }`}
        title={collapsed ? label : ''}
      >
        <Icon className={`w-6 h-6 ${isActive ? 'text-gray-100' : isOpen ? 'text-gray-200' : 'text-gray-200'}`} />
        {!collapsed && (
          <>
            <span className={`ml-3 ${isActive ? 'text-gray-100' : isOpen ? 'text-gray-300' : 'text-gray-300'}`}>{label}</span>
            {isOpen ?
              <ChevronDownIcon className={`ml-auto w-4 h-4 ${isActive ? 'text-gray-300' : 'text-gray-400'}`} /> :
              <ChevronRightIcon className={`ml-auto w-4 h-4 ${isActive ? 'text-gray-300' : 'text-gray-400'}`} />
            }
          </>
        )}
      </button>
      {isOpen && !collapsed && (
        <ul className="pt-1 pb-2 ml-3 mt-1 space-y-0.5 border-l border-gray-600">
          {items.map((subItem, subIndex) => (
            <SubItem 
              key={subIndex} 
              subItem={subItem} 
              openNestedSubmenu={openNestedSubmenu}
              setOpenNestedSubmenu={setOpenNestedSubmenu}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const SubItem = ({ subItem, openNestedSubmenu, setOpenNestedSubmenu }) => {
  const location = window.location.pathname;
  const hasNestedItems = subItem.subItems && subItem.subItems.length > 0;
  const isNestedOpen = openNestedSubmenu === subItem.name;
  const isActive = hasNestedItems 
    ? subItem.subItems.some(nested => {
        // Handle query params in path
        const nestedPath = nested.path.split('?')[0];
        const currentPath = location.split('?')[0];
        return currentPath === nestedPath;
      })
    : (() => {
        // Handle query params in path
        const itemPath = subItem.path.split('?')[0];
        const currentPath = location.split('?')[0];
        return currentPath === itemPath;
      })();

  if (hasNestedItems) {
    return (
      <li>
        <button
          onClick={() => setOpenNestedSubmenu(isNestedOpen ? null : subItem.name)}
          className={`ml-2 text-sm font-medium transition-all duration-200 rounded-md px-3 py-2 flex items-center w-full justify-between ${
            isActive || isNestedOpen
              ? 'bg-gray-700/60 text-gray-200 border-l-4 border-l-green-500'
              : 'text-gray-300 hover:bg-gray-700/30 hover:text-gray-200'
          }`}
        >
          <span>{subItem.name}</span>
          {isNestedOpen ? (
            <ChevronDownIcon className="w-3.5 h-3.5 ml-2 text-gray-300" />
          ) : (
            <ChevronRightIcon className="w-3.5 h-3.5 ml-2 text-gray-300" />
          )}
        </button>
        {isNestedOpen && (
          <ul className="pt-1 pb-1 ml-2 mt-0.5 space-y-0.5 border-l border-gray-600">
            {subItem.subItems.map((nestedItem, nestedIndex) => {
              const isNestedActive = location === nestedItem.path;
              return (
                <li key={nestedIndex}>
                  <Link 
                    to={nestedItem.path} 
                    className={`ml-2 text-xs font-medium transition-all duration-200 rounded-md px-2.5 py-1.5 block ${
                      isNestedActive
                        ? 'bg-gray-700/60 text-gray-200 border-l-4 border-l-orange-500'
                        : 'text-gray-300 hover:bg-gray-700/30 hover:text-gray-200'
                    }`}
                  >
                    {nestedItem.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li>
      <Link 
        to={subItem.path} 
        className={`ml-2 text-sm font-medium transition-all duration-200 rounded-md px-3 py-2 block ${
          isActive
            ? 'bg-gray-700/60 text-gray-200 border-l-4 border-l-blue-500'
            : 'text-gray-300 hover:bg-gray-700/30 hover:text-gray-200'
        }`}
      >
        {subItem.name}
      </Link>
    </li>
  );
};